import { ErrorHandlerService } from '@app-services/error-handler.service';
import { effect, inject, Injector, signal, untracked } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  catchError,
  Observable,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { IAuthAccessToken } from '@app-auth/data-access/entities/auth.interface';
import { AuthStore } from '@app-auth/data-access/auth.store';
import { ProfileStore } from '@app-profile/data-access/profile.store';
import { PresentationStore } from '@app-presentation/data-access/presentation.store';
export class TokenInterceptor implements HttpInterceptor {
  private authStore = inject(AuthStore);
  private profileStore = inject(ProfileStore);
  private errorHandlerService = inject(ErrorHandlerService);
  // Resolvido sob demanda no 403: injetar o PresentationStore direto no field
  // criaria um ciclo de DI (PresentationStore -> WorkoutStore -> HttpClient ->
  // interceptors -> este interceptor) durante a construção do HttpClient.
  private injector = inject(Injector);
  // Emits the outcome of the *next* refresh: true = refreshed, false = failed.
  // A plain Subject (not BehaviorSubject) is used on purpose: a new 401 must wait
  // for a fresh refresh and never receive a replayed/previous token.
  private refreshSubject = new Subject<boolean>();
  private reTryRefreshToken = 0;
  private isRefreshingToken = false;
  private readonly logoutOn403Endpoints = [
    { method: 'GET', url: '/OwnerSettings/GetAsync' },
    { method: 'GET', url: '/Person/GetUserDataAsync' },
    { method: 'GET', url: '/Contas/Usuario' },
    { method: 'POST', url: '/Auth/Refresh' },
  ];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.addAuthToken(request)).pipe(
      catchError((error, retryRequest) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next, retryRequest);
        }
        if (error instanceof HttpErrorResponse && error.status === 400) {
          this.handle400Error(error);
        }
        if (error instanceof HttpErrorResponse && error.status === 403) {
          if (this.shouldLogoutOn403(request)) {
            this.errorHandlerService.handleInactiveSubscription();
            this.profileStore.clearStore();
            this.authStore.logout();
          } else if (request.method === 'GET' && request.url.endsWith('/Subscription')) {
            // GET /Subscription (getSubscriptionById) responde 403 indevidamente:
            // o backend criou uma permissão que não deveria existir (o app não usa
            // permissões). Abre o gate como feedback visual, sem deslogar. Injeção
            // lazy via Injector evita ciclo de DI.
            this.injector.get(PresentationStore).denyAccess();
          } else {
            // O único 403 legítimo é assinatura inativa (tratado acima). Qualquer
            // outro é o backend setando 403 por engano — mostra alerta genérico
            // para o usuário nunca ficar sem feedback.
            this.errorHandlerService.handleForbidden();
          }
        }
        return throwError(() => error);
      })
    );
  }

  private shouldLogoutOn403(request: HttpRequest<any>): boolean {
    return this.logoutOn403Endpoints.some(({ method, url }) => request.method === method && request.url.includes(url));
  }

  private addAuthToken(request: HttpRequest<any>, token?: IAuthAccessToken | null) {
    const language = signal('pt-BR')();
    let headers = new HttpHeaders().set('Accept-Language', language);

    if (!request.withCredentials) {
      return request.clone({
        headers,
      });
    }

    const _token = token ?? this.authStore.AuthData();
    if (_token?.Token) {
      headers = headers.set('Authorization', `Bearer ${_token?.Token}`);
      return request.clone({
        withCredentials: false,
        headers,
      });
    }
    return request;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler, retryRequest: Observable<any>) {
    // Start a refresh only when one isn't already running. Concurrent 401s (a page
    // like the feed fires many authed requests at once) skip this block and simply
    // wait for the same refresh, so reTryRefreshToken tracks real refresh attempts
    // instead of the number of in-flight requests — otherwise a burst of requests
    // would trip the logout below before the refresh even completes.
    if (!this.authStore.refreshTokenLoading() && !this.isRefreshingToken) {
      if (this.reTryRefreshToken > 5 || !this.authStore.isLoggedIn()) {
        this.reTryRefreshToken = 0;
        this.isRefreshingToken = false;
        this.profileStore.clearStore();
        this.authStore.logout();
        return throwError(() => new Error('Unable to refresh authentication token'));
      }

      this.reTryRefreshToken++;
      this.isRefreshingToken = true;
      this.authStore.refreshToken(this.authStore.AuthData()?.TokenId!);
    }

    // Wait for the *next* refresh outcome, then retry. We read the freshly stored
    // token via addAuthToken (no token argument) at retry time, so the request
    // always carries the new token instead of being sent without it / with the
    // expired one. take(1) tears down this subscription once the retry fires.
    return this.refreshSubject.pipe(
      take(1),
      switchMap((refreshed) => {
        this.isRefreshingToken = false;

        if (!refreshed) {
          return throwError(() => new Error('Token refresh failed'));
        }

        return next.handle(this.addAuthToken(request)).pipe(
          // Reset the attempt counter once a retry actually succeeds.
          tap((event) => {
            if (event instanceof HttpResponse) this.reTryRefreshToken = 0;
          })
        );
      })
    );
  }

  private handle400Error(error: HttpErrorResponse) {
    const errorMessage = error.error ? error.error.error : '';

    if (error && error?.status === 400 && (errorMessage === 'invalid_grant' || errorMessage === 'invalid_clientId')) {
      this.profileStore.clearStore();
      return this.authStore.logout();
    }

    if (error?.status === 400 && error?.error?.errors?.DomainValidations?.[0] === 'Não foi encontrado o token.') {
      this.profileStore.clearStore();
      return this.authStore.logout();
    }

    if (error?.error?.ValidationResult?.Errors?.[0]?.ErrorMessage === 'Localize.UserService.Msg.InvalidToken') {
      this.profileStore.clearStore();
      return this.authStore.logout();
    }
  }

  private readonly observeRefreshTokenError = effect(() => {
    if (this.authStore.refreshTokenError()) {
      untracked(() => this.refreshSubject.next(false));
    }
  });

  private readonly observeRefreshTokenLoaded = effect(() => {
    if (this.authStore.refreshTokenLoaded()) {
      untracked(() => this.refreshSubject.next(true));
    }
  });
}
